import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable()
export class TeamService {
  constructor(private apollo: Apollo, private http: HttpClient) {}

  getAllTeamProfiles(obj?) {
    let variables: any = {
      first: 20,
      after: null,
      searchParam: null,
    };

    if (obj) {
      variables = {
        first: obj.paginationData.pageSize || 20,
        after: obj.paginationData.after ? obj.paginationData.after.endCursor : null,
        searchParam: obj.searchParam || null,
      };
    }

    return this.apollo.watchQuery({
      query: gql`
        query GetTeamProfiles($first: Int, $after: String, $searchParam: String) {
          teamProfiles(first: $first, after: $after, searchParam: $searchParam) {
            totalCount
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                name
                designation
                location
                status
                dzongkhaName
                featuredPhoto(w: 400, h: 400, fit: "crop") {
                  id
                  url
                }
              }
            }
          }
        }
      `,
      variables,
      errorPolicy: 'all',
    }).valueChanges;
  }

  getTeamProfile(id: string) {
    return this.apollo.watchQuery({
      query: gql`
        query GetTeamProfile($id: ID!) {
          teamProfile(id: $id) {
            id
            name
            designation
            location
            description
            status
            yearsOfExperience
            dzongkhaName
            funFact
            hobbies
            awards
            certifications
            publications {
              title
              url
            }
            featured_in {
              title
              url
            }
            visitedDistricts
            social {
              linkedin
              twitter
              instagram
              facebook
            }
            audioFileId
            audioFile {
              id
              url
            }
            featuredPhoto(w: 800, h: 800, fit: "crop") {
              id
              url
              filename
              mimetype
            }
            photos(w: 1200, h: 800, fit: "clip") {
              id
              url
              filename
              mimetype
            }
          }
        }
      `,
      variables: { id },
      errorPolicy: 'all',
    }).valueChanges;
  }

  createTeamProfile(teamProfile) {
    return this.apollo.mutate({
      mutation: gql`
        mutation CreateTeamProfile($teamProfile: TeamProfileInput!) {
          createTeamProfile(teamProfile: $teamProfile) {
            id
            name
            status
          }
        }
      `,
      variables: { teamProfile },
    });
  }

  updateTeamProfile(teamUpdate) {
    return this.apollo.mutate({
      mutation: gql`
        mutation UpdateTeamProfile($teamUpdate: TeamUpdateInput!) {
          updateTeamProfile(teamUpdate: $teamUpdate) {
            success
          }
        }
      `,
      variables: { teamUpdate },
    });
  }

  removeTeamMember(id: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation RemoveTeamMember($id: String!) {
          removeTeamMember(id: $id) {
            success
          }
        }
      `,
      variables: { id },
    });
  }

  // --- Visited Districts ---

  getDistrictOptions() {
    return this.apollo.query({
      query: gql`
        query GetDistrictOptions {
          __type(name: "BhutanDistrict") {
            enumValues {
              name
              description
            }
          }
        }
      `,
      fetchPolicy: 'network-only'
    });
  }

  // --- Guest Reviews ---

  getTeamProfileReviews(teamProfileId: string) {
    return this.apollo.watchQuery({
      query: gql`
        query GetTeamProfileWithReviews($id: ID!) {
          teamProfile(id: $id) {
            id
            reviews {
              id
              review
              clientName
              clientLocation
              tripType
              status
            }
          }
        }
      `,
      variables: { id: teamProfileId },
      fetchPolicy: 'network-only'
    }).valueChanges;
  }

  createTeamProfileReview(review) {
    return this.apollo.mutate({
      mutation: gql`
        mutation CreateReview($review: TeamProfileReviewInput!) {
          createTeamProfileReview(review: $review) {
            id
            text
            reviewerName
            status
          }
        }
      `,
      variables: { review },
    });
  }

  updateTeamProfileReview(id: string, review: any) {
    return this.apollo.mutate({
      mutation: gql`
        mutation UpdateTeamProfileReview($id: ID!, $review: TeamProfileReviewUpdateInput!) {
          updateTeamProfileReview(id: $id, review: $review) {
            success
          }
        }
      `,
      variables: { id, review },
    });
  }

  removeTeamProfileReview(id: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation RemoveTeamProfileReview($id: ID!) {
          removeTeamProfileReview(id: $id) {
            success
          }
        }
      `,
      variables: { id },
    });
  }


  setFeaturedPhoto(teamProfileId: string, fileId: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation SetFeaturedPhoto($teamProfileId: ID!, $fileId: ID!) {
          setFeaturedPhoto(teamProfileId: $teamProfileId, fileId: $fileId) {
            success
          }
        }
      `,
      variables: { teamProfileId, fileId },
    });
  }

  createFile(file) {
    return this.apollo.mutate({
      mutation: gql`
        mutation CreateFile($file: FileCreateInput!) {
          createFile(file: $file) {
            id
            url
          }
        }
      `,
      variables: { file },
    });
  }

  addToGallery(teamProfileId: string, imageId: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation AddToGallery($data: AddToGallery!) {
          addToGallery(data: $data) {
            success
          }
        }
      `,
      variables: { data: { teamProfileId, imageId } },
    });
  }

  removeFromGallery(teamProfileId: string, imageId: string) {
    return this.apollo.mutate({
      mutation: gql`
        mutation RemoveFromGallery($data: RemoveFromGallery!) {
          removeFromGallery(data: $data) {
            success
          }
        }
      `,
      variables: { data: { teamProfileId, imageId } },
    });
  }

  issuePresignedURL(fileType: string) {
    const options = { params: new HttpParams().set('file-type', fileType) };
    return this.http.get<any>(environment.server + 'issuePresignedURL', options);
  }

  uploadToS3(signedRequest: string, file: File) {
    return this.http.put(signedRequest, file, {
      headers: { 'Content-Type': file.type },
    });
  }

  uploadPhotoMultipart(file: File, teamProfileId: string) {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('teamProfileId', teamProfileId);
    return this.http.post(environment.server + 'photo/upload', formData, { responseType: 'text' });
  }
}



// WEBPACK FOOTER //
// /Users/ugyen/Documents/Bhutan-office/my_bhutan/mybhutan-travel/src-admin/app/services/team.service.ts